import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Without rejectWithValue, an axios 4xx/5xx rejects the thunk and `payload`
// comes back undefined — so the caller could never read the server's actual
// message and always fell through to a generic "Failed to ..." toast. Every
// thunk here captures the response body instead.
const withServerError = (fn) => async (arg, { rejectWithValue }) => {
  try {
    return await fn(arg);
  } catch (error) {
    return rejectWithValue(
      error.response?.data || { success: false, message: error.message }
    );
  }
};

// Normalizes either shape into the server's response body:
//   fulfilled -> payload is the axios response, body is payload.data
//   rejected  -> payload IS the body (via rejectWithValue)
export const violationBody = (res) => res?.payload?.data ?? res?.payload ?? null;

export const ReportViolation = createAsyncThunk(
  "violation/report",
  withServerError(({ tenantId, propertyId, reason, description, severity, evidenceFiles }) => {
    // Evidence goes as separate `evidence1`, `evidence2`, ... fields, same
    // convention as tenant registration's cnicFront/cnicBack, rather than one
    // repeated field name.
    if (evidenceFiles?.length) {
      const formData = new FormData();
      formData.append("tenantId", tenantId);
      formData.append("propertyId", propertyId);
      formData.append("reason", reason);
      formData.append("description", description);
      formData.append("severity", severity);
      evidenceFiles.forEach((file, i) => formData.append(`evidence${i + 1}`, file));
      return axios.post(`${BASE_URL}/agreement-violations`, formData);
    }

    return axios.post(`${BASE_URL}/agreement-violations`, {
      tenantId,
      propertyId,
      reason,
      description,
      severity,
    });
  })
);

export const GetViolations = createAsyncThunk(
  "violation/getForTenantFlat",
  async ({ tenantId, propertyId }) => {
    const response = await axios.get(
      `${BASE_URL}/agreement-violations/${tenantId}/${propertyId}`
    );
    return response;
  }
);

export const UpdateViolationStatus = createAsyncThunk(
  "violation/updateStatus",
  async ({ id, status }) => {
    const response = await axios.put(
      `${BASE_URL}/agreement-violations/${id}/status`,
      { status }
    );
    return response;
  }
);

export const EscalateViolation = createAsyncThunk(
  "violation/escalate",
  withServerError(({ id, dueDate }) =>
    axios.post(`${BASE_URL}/agreement-violations/${id}/escalate`, { dueDate })
  )
);

// Tenant's own record, across every property they rent.
export const GetTenantViolations = createAsyncThunk(
  "violation/getForTenant",
  async ({ tenantId }) => {
    const response = await axios.get(
      `${BASE_URL}/agreement-violations/tenant/${tenantId}`
    );
    return response;
  }
);

// Tenant owns the breach and commits to not repeating it. Deliberately not a
// "reject" — the tenant can't dismiss a violation the landlord witnessed.
export const SubmitApology = createAsyncThunk(
  "violation/apologize",
  withServerError(({ id, message }) =>
    axios.post(`${BASE_URL}/agreement-violations/${id}/apologize`, { message })
  )
);

// Landlord — and only the landlord — decides on that apology.
export const DecideApology = createAsyncThunk(
  "violation/apologyDecision",
  withServerError(({ id, decision, note }) =>
    axios.post(`${BASE_URL}/agreement-violations/${id}/apology-decision`, {
      decision,
      note,
    })
  )
);

const AgreementViolationSlice = createSlice({
  name: "agreementViolation",
  initialState: {
    loading: false,
    violations: [],
    error: "",
  },
  reducers: {
    clearViolationError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetViolations.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(GetViolations.fulfilled, (state, action) => {
        state.loading = false;
        state.violations = action.payload?.data?.data || [];
      })
      .addCase(GetViolations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(ReportViolation.fulfilled, (state, action) => {
        const created = action.payload?.data?.data;
        if (created) state.violations = [created, ...state.violations];
      })
      .addCase(UpdateViolationStatus.fulfilled, (state, action) => {
        const updated = action.payload?.data?.data;
        if (updated) {
          state.violations = state.violations.map((v) =>
            v._id === updated._id ? updated : v
          );
        }
      })
      .addCase(EscalateViolation.fulfilled, (state, action) => {
        const updated = action.payload?.data?.data?.violation;
        if (updated) {
          state.violations = state.violations.map((v) =>
            v._id === updated._id ? updated : v
          );
        }
      })
      .addCase(GetTenantViolations.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(GetTenantViolations.fulfilled, (state, action) => {
        state.loading = false;
        state.violations = action.payload?.data?.data || [];
      })
      .addCase(GetTenantViolations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(SubmitApology.fulfilled, (state, action) => {
        const updated = action.payload?.data?.data;
        if (updated) {
          state.violations = state.violations.map((v) =>
            v._id === updated._id ? updated : v
          );
        }
      })
      .addCase(DecideApology.fulfilled, (state, action) => {
        const updated = action.payload?.data?.data?.violation;
        if (updated) {
          state.violations = state.violations.map((v) =>
            v._id === updated._id ? updated : v
          );
        }
      });
  },
});

export const { clearViolationError } = AgreementViolationSlice.actions;
export default AgreementViolationSlice.reducer;
